import { useState, useEffect } from 'react';
import { supabase } from './utils/supabase';
import Auth from './components/Auth';
import { 
  Plus, 
  Trash2, 
  CheckCircle2, 
  Clock, 
  ChevronRight, 
  ChevronLeft,
  Calendar,
  AlertCircle,
  LogOut,
  Loader2,
  LayoutDashboard,
  CheckSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

type Status = 'todo' | 'doing' | 'done';

interface Todo {
  id: string;
  name: string;
  status: Status;
  created_at: string;
  user_id: string;
}

const statusConfig = {
  todo: { label: '할 일', icon: AlertCircle, color: '#3b82f6' },
  doing: { label: '진행 중', icon: Clock, color: '#f59e0b' },
  done: { label: '완료된 일', icon: CheckCircle2, color: '#10b981' }
};

function App() {
  const [session, setSession] = useState<any>(null);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  useEffect(() => {
    // 보안 세션 확인 타임아웃 (3초 내에 응답 없으면 강제 진행)
    const timer = setTimeout(() => {
      if (isAuthChecking) {
        console.warn('Auth check timed out. Showing home screen.');
        setIsAuthChecking(false);
      }
    }, 3000);

    // 세션 초기 확인
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Session fetched:', session ? 'User logged in' : 'No user');
      setSession(session);
      if (session) fetchTodos();
      setIsAuthChecking(false);
      clearTimeout(timer);
    }).catch(err => {
      console.error('Session fetch error:', err);
      setIsAuthChecking(false);
      clearTimeout(timer);
    });

    // 인증 상태 변경 감지
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchTodos();
      else {
        setTodos([]);
        setIsLoading(false);
      }
      setIsAuthChecking(false);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const fetchTodos = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('todos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTodos(data || []);
    } catch (error) {
      console.error('Error fetching todos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const addTodo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || !session) return;

    const { data, error } = await supabase
      .from('todos')
      .insert([
        { 
          name: inputValue.trim(), 
          status: 'todo', 
          user_id: session.user.id 
        }
      ])
      .select();

    if (error) {
      console.error('Error adding todo:', error);
    } else if (data) {
      setTodos([data[0], ...todos]);
      setInputValue('');
    }
  };

  const deleteTodo = async (id: string) => {
    const { error } = await supabase
      .from('todos')
      .delete()
      .eq('id', id);

    if (error) console.error('Error deleting todo:', error);
    else setTodos(todos.filter(todo => todo.id !== id));
  };

  const moveStatus = async (id: string, direction: 'next' | 'prev') => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;

    let nextStatus: Status = todo.status;
    if (direction === 'next') {
      if (todo.status === 'todo') nextStatus = 'doing';
      else if (todo.status === 'doing') nextStatus = 'done';
    } else {
      if (todo.status === 'done') nextStatus = 'doing';
      else if (todo.status === 'doing') nextStatus = 'todo';
    }

    const { error } = await supabase
      .from('todos')
      .update({ status: nextStatus })
      .eq('id', id);

    if (error) console.error('Error updating status:', error);
    else setTodos(todos.map(t => t.id === id ? { ...t, status: nextStatus } : t));
  };

  const getFilteredTodos = (status: Status) => 
    todos.filter(todo => todo.status === status);

  // 1. 인증 상태 확인 중일 때 로딩 화면
  if (isAuthChecking) {
    return (
      <div className="initial-loading">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="loading-content"
        >
          <div className="app-logo">
            <CheckSquare size={48} color="#3b82f6" />
          </div>
          <h2>My Workspace</h2>
          <Loader2 className="animate-spin" size={32} color="#3b82f6" />
          <p>보안 세션을 확인하는 중입니다...</p>
        </motion.div>
      </div>
    );
  }

  // 2. 로그인하지 않은 사용자에게 홈 화면 + 인증창 노출
  if (!session) {
    return (
      <div className="home-container">
        <div className="home-hero">
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="hero-text"
          >
            <div className="badge">Professional Task Manager</div>
            <h1>스마트한 협업의 시작,<br/><span>To-do 대시보드</span></h1>
            <p>실무에서 바로 사용하는 할 일 관리 시스템을 경험하세요.</p>
          </motion.div>
          <Auth />
        </div>
        <footer className="home-footer">
          <p>© 2024 My Workspace. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // 3. 로그인된 사용자의 메인 대시보드
  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-top">
          <div className="user-info">
            <span className="user-email">{session.user.email}</span>
            <button onClick={handleLogout} className="logout-btn" title="로그아웃">
              <LogOut size={18} />
            </button>
          </div>
        </div>
        <div className="header-content">
          <div className="title-area">
            <LayoutDashboard className="title-icon" size={32} />
            <h1>Dashboard</h1>
          </div>
          <p>오늘의 업무 진행 상황을 한눈에 확인하세요.</p>
        </div>
        <form onSubmit={addTodo} className="input-group">
          <input 
            type="text" 
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="새로운 할 일을 입력하여 업무 생산성을 높이세요..."
            className="todo-input"
          />
          <button type="submit" className="add-button">
            <Plus size={20} />
            <span>등록</span>
          </button>
        </form>
      </header>

      {isLoading ? (
        <div className="loading-container">
          <Loader2 className="animate-spin" size={40} />
          <p>데이터를 불러오고 있습니다...</p>
        </div>
      ) : (
        <main className="board">
          {(['todo', 'doing', 'done'] as Status[]).map((status) => {
            const config = statusConfig[status];
            const filtered = getFilteredTodos(status);
            
            return (
              <div key={status} className={`column ${status}`}>
                <div className="column-header">
                  <div className="column-title" style={{ color: config.color }}>
                    <config.icon size={20} />
                    <h2>{config.label}</h2>
                    <span className="count">{filtered.length}</span>
                  </div>
                </div>
                
                <div className="column-content">
                  <AnimatePresence mode="popLayout">
                    {filtered.length > 0 ? (
                      filtered.map((todo) => (
                        <motion.div
                          key={todo.id}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="todo-card"
                        >
                          <div className="todo-card-text">{todo.name}</div>
                          <div className="todo-card-footer">
                            <div className="todo-date">
                              <Calendar size={14} />
                              {new Date(todo.created_at).toLocaleDateString()}
                            </div>
                            <div className="actions">
                              {status !== 'todo' && (
                                <button 
                                  onClick={() => moveStatus(todo.id, 'prev')}
                                  className="action-btn prev"
                                  title="이전 단계로"
                                >
                                  <ChevronLeft size={16} />
                                </button>
                              )}
                              <button 
                                onClick={() => deleteTodo(todo.id)}
                                className="action-btn delete"
                                title="삭제"
                              >
                                <Trash2 size={16} />
                              </button>
                              {status !== 'done' && (
                                <button 
                                  onClick={() => moveStatus(todo.id, 'next')}
                                  className="action-btn next"
                                  title="다음 단계로"
                                >
                                  <ChevronRight size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="empty-state">
                        아직 기록된 항목이 없습니다.
                      </div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </main>
      )}
    </div>
  );
}

export default App;
